import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import {
  Bar,
  Cell,
  CartesianGrid,
  ComposedChart,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from 'recharts';
import { cn } from '@c2fo/liquidity';
import colors from '@c2fo/liquidity/colors';
import useLocaleFormat from '@/utils/useLocaleFormat';
import dataSearchUrl from './data_search.svg?url';
import type { CashflowPeriodItem } from './useBuyerCashflow';

const normalizeNegativeAmount = (raw: number): number => -Math.abs(raw);

const CHART_COLORS = {
  CASH_INFLOWS: colors.green[600],
  PAST_DUE: colors.red[500],
  CASH_OUTFLOWS: colors.orange[500],
  // Tooltip-only breakdown colors
  PAID_ON_TERMS: colors.green[500],
  PAID_EARLY: colors.lime[500],
  FUTURE_DUE: colors.lightGreen[500],
} as const;

interface ChartDataPoint {
  periodStart: string;
  label: string;
  labelFull: string;
  // Bar series
  cashInflows: number;
  pastDue: number;
  cashOutflows: number;
  currency: string;
  byCustomer: CashflowPeriodItem['byCustomer'];
  // Tooltip-only inflow breakdown (not used as Recharts dataKeys)
  breakdown: {
    paidOnTerms: number;
    paidEarly: number;
    futureDue: number;
  };
}

const getNetCashForPeriod = (period: {
  paidOnTermsAmount: number;
  c2foEarlyPaidAmount: number;
  c2foEarlyPaidDiscount: number;
  totalDeductionAmount: number;
  futureDueAmount: number;
}) =>
  period.paidOnTermsAmount +
  period.c2foEarlyPaidAmount +
  period.futureDueAmount +
  normalizeNegativeAmount(period.c2foEarlyPaidDiscount) +
  normalizeNegativeAmount(period.totalDeductionAmount);

export const buildChartData = (periods: CashflowPeriodItem[], currency: string): ChartDataPoint[] =>
  periods.map(({ totals, periodStart, byCustomer }) => ({
    periodStart,
    label: format(parseISO(periodStart), 'MMM d'),
    labelFull: format(parseISO(periodStart), 'MMM dd, yyyy'),
    cashInflows: totals.paidOnTermsAmount + totals.c2foEarlyPaidAmount + totals.futureDueAmount,
    pastDue: totals.pastDueAmount,
    // Cash Outflows = customer-applied deductions only (excludes early pay discounts)
    cashOutflows: normalizeNegativeAmount(totals.totalDeductionAmount),
    currency,
    byCustomer,
    breakdown: {
      paidOnTerms: totals.paidOnTermsAmount,
      paidEarly: totals.c2foEarlyPaidAmount,
      futureDue: totals.futureDueAmount,
    },
  }));

const CustomerBreakdown = ({
  customers,
  customerNames,
  currency,
  formatCurrency,
}: {
  customers: CashflowPeriodItem['byCustomer'];
  customerNames: Record<string, string>;
  currency: string;
  formatCurrency: (value: number, currency: string) => string;
}) => {
  const sorted = [...customers].sort((a, b) => getNetCashForPeriod(b) - getNetCashForPeriod(a));

  if (sorted.length === 0) return null;

  return (
    <div className="mt-2 border-t border-gray-200 pt-2">
      <p className="mb-1 text-xs font-semibold text-gray-500">By Customer</p>
      {sorted.map((c) => {
        const net = getNetCashForPeriod(c);
        const displayName = customerNames[c.makerOrganizationUuid] ?? c.makerOrganizationUuid;
        return (
          <div key={c.makerOrganizationUuid} className="flex items-center justify-between gap-4">
            <span className="max-w-[140px] truncate text-xs text-gray-700">{displayName}</span>
            <span className="text-xs font-medium text-gray-900">{formatCurrency(net, currency)}</span>
          </div>
        );
      })}
    </div>
  );
};

interface TooltipRowProps {
  label: string;
  value: number;
  color: string;
  currency: string;
  formatCurrency: (value: number, currency: string) => string;
  indent?: boolean;
}

const TooltipRow = ({ label, value, color, currency, formatCurrency, indent }: TooltipRowProps) => (
  <div className={cn('flex items-center justify-between gap-4', indent && 'pl-3')}>
    <div className="flex items-center gap-1">
      <span className="inline-block h-2 w-2 shrink-0 rounded-sm" style={{ backgroundColor: color }} />
      <span className={cn(indent ? 'text-gray-500' : 'text-gray-700')}>{label}</span>
    </div>
    <span className={cn('font-medium', indent ? 'text-gray-600' : 'text-gray-900')}>
      {formatCurrency(value, currency)}
    </span>
  </div>
);

const CustomTooltip = ({
  active,
  payload,
  currency,
  customerNames,
  formatCurrency,
  labels,
}: TooltipProps<number, string> & {
  currency: string;
  customerNames: Record<string, string>;
  formatCurrency: (value: number, currency: string) => string;
  labels: {
    cashInflows: string;
    pastDue: string;
    cashOutflows: string;
    paidOnTerms: string;
    paidEarly: string;
    futureDue: string;
  };
}) => {
  if (!active || !payload?.length) return null;

  const point = payload[0]?.payload as ChartDataPoint;
  const { breakdown } = point;

  const inflowDetails = [
    { label: labels.paidOnTerms, value: breakdown.paidOnTerms, color: CHART_COLORS.PAID_ON_TERMS },
    { label: labels.paidEarly, value: breakdown.paidEarly, color: CHART_COLORS.PAID_EARLY },
    { label: labels.futureDue, value: breakdown.futureDue, color: CHART_COLORS.FUTURE_DUE },
  ].filter((r) => r.value !== 0);

  return (
    <div
      style={{
        backgroundColor: 'white',
        border: `1px solid ${colors.gray[200]}`,
        borderRadius: '8px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        fontSize: 12,
        padding: '10px 14px',
        minWidth: 200,
      }}
    >
      <p className="mb-1 font-semibold text-gray-700">{point.labelFull}</p>

      {point.cashInflows !== 0 && (
        <>
          <TooltipRow
            label={labels.cashInflows}
            value={point.cashInflows}
            color={CHART_COLORS.CASH_INFLOWS}
            currency={currency}
            formatCurrency={formatCurrency}
          />
          {inflowDetails.map((r) => (
            <TooltipRow
              key={r.label}
              label={r.label}
              value={r.value}
              color={r.color}
              currency={currency}
              formatCurrency={formatCurrency}
              indent
            />
          ))}
        </>
      )}
      {point.pastDue !== 0 && (
        <TooltipRow
          label={labels.pastDue}
          value={point.pastDue}
          color={CHART_COLORS.PAST_DUE}
          currency={currency}
          formatCurrency={formatCurrency}
        />
      )}
      {point.cashOutflows !== 0 && (
        <TooltipRow
          label={labels.cashOutflows}
          value={point.cashOutflows}
          color={CHART_COLORS.CASH_OUTFLOWS}
          currency={currency}
          formatCurrency={formatCurrency}
        />
      )}

      <CustomerBreakdown
        customers={point.byCustomer}
        customerNames={customerNames}
        currency={currency}
        formatCurrency={formatCurrency}
      />
    </div>
  );
};

interface FinanceIQChartProps {
  periods: CashflowPeriodItem[];
  currency: string;
  todayString: string;
  customerNames: Record<string, string>;
  onBarClick?: (periodStart: string, byCustomer: CashflowPeriodItem['byCustomer'], currency: string) => void;
}

const FinanceIQChart = ({ periods, currency, todayString, customerNames, onBarClick }: FinanceIQChartProps) => {
  const { t } = useTranslation();
  const { asCurrencyCompact } = useLocaleFormat();

  const data = useMemo(() => buildChartData(periods, currency), [periods, currency]);

  const hasData = data.some((d) => d.cashInflows !== 0 || d.pastDue !== 0 || d.cashOutflows !== 0);

  const hasCashInflows = data.some((d) => d.cashInflows !== 0);
  const hasPastDue = data.some((d) => d.pastDue !== 0);
  const hasCashOutflows = data.some((d) => d.cashOutflows !== 0);

  if (!hasData) {
    return (
      <div className="flex flex-col items-center gap-3 py-8">
        <img src={dataSearchUrl} alt="" aria-hidden="true" className="h-36 w-36" />
        <div className="flex flex-col items-center gap-1">
          <p className="text-base font-semibold text-gray-700">{t('financeiq.chart.emptyState')}</p>
          <p className="max-w-md text-center text-sm text-gray-500">{t('financeiq.chart.emptyStateSubtext')}</p>
        </div>
      </div>
    );
  }

  const fmtAxis = (value: number) => asCurrencyCompact(value, currency);

  const handleChartClick = onBarClick
    ? (chartData: unknown) => {
        if (
          !chartData ||
          typeof chartData !== 'object' ||
          !('activePayload' in chartData) ||
          !Array.isArray(chartData.activePayload) ||
          chartData.activePayload.length === 0
        ) {
          return;
        }

        const point = chartData.activePayload[0].payload as ChartDataPoint;
        onBarClick(point.periodStart, point.byCustomer, point.currency);
      }
    : undefined;

  const activeLegendLabels = new Set<string>();
  if (hasCashInflows) activeLegendLabels.add(t('financeiq.chart.legend.cashInflows'));
  if (hasPastDue) activeLegendLabels.add(t('financeiq.chart.legend.pastDue'));
  if (hasCashOutflows) activeLegendLabels.add(t('financeiq.chart.legend.cashOutflows'));

  return (
    <div className="relative w-full" style={{ height: 280 }}>
      <div className="absolute inset-0">
        <ResponsiveContainer>
          <ComposedChart
            data={data}
            margin={{ top: 20, right: 5, bottom: 0, left: 5 }}
            stackOffset="sign"
            onClick={handleChartClick}
            style={{ cursor: onBarClick ? 'pointer' : 'default' }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={colors.gray[200]} vertical={false} />

            <XAxis
              dataKey="periodStart"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 11, fill: colors.gray[600] }}
              ticks={data.filter((_, i) => i % 2 === 0).map((d) => d.periodStart)}
              tickFormatter={(value) => data.find((d) => d.periodStart === value)?.label ?? value}
            />

            <YAxis
              yAxisId="left"
              orientation="left"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 11, fill: colors.gray[600] }}
              tickFormatter={fmtAxis}
            />

            <Tooltip
              content={
                <CustomTooltip
                  currency={currency}
                  customerNames={customerNames}
                  formatCurrency={asCurrencyCompact}
                  labels={{
                    cashInflows: t('financeiq.chart.legend.cashInflows'),
                    pastDue: t('financeiq.chart.legend.pastDue'),
                    cashOutflows: t('financeiq.chart.legend.cashOutflows'),
                    paidOnTerms: t('financeiq.chart.legend.paidOnTerms'),
                    paidEarly: t('financeiq.chart.legend.paidEarly'),
                    futureDue: t('financeiq.chart.legend.futureDue'),
                  }}
                />
              }
            />

            <ReferenceLine
              yAxisId="left"
              x={todayString}
              stroke={colors.gray[500]}
              strokeDasharray="4 4"
              strokeWidth={2}
              label={{
                value: t('financeiq.chart.today'),
                position: 'top',
                fontSize: 11,
                fill: colors.gray[600],
              }}
            />

            {hasCashInflows && (
              <Bar
                yAxisId="left"
                dataKey="cashInflows"
                name={t('financeiq.chart.legend.cashInflows')}
                stackId="stack"
                fill={CHART_COLORS.CASH_INFLOWS}
                maxBarSize={16}
              >
                {data.map((d) => (
                  <Cell key={d.periodStart} fillOpacity={d.periodStart < todayString ? 0.4 : 1} />
                ))}
              </Bar>
            )}
            {hasPastDue && (
              <Bar
                yAxisId="left"
                dataKey="pastDue"
                name={t('financeiq.chart.legend.pastDue')}
                stackId="stack"
                fill={CHART_COLORS.PAST_DUE}
                maxBarSize={16}
              >
                {data.map((d) => (
                  <Cell key={d.periodStart} fillOpacity={d.periodStart < todayString ? 0.4 : 1} />
                ))}
              </Bar>
            )}
            {hasCashOutflows && (
              <Bar
                yAxisId="left"
                dataKey="cashOutflows"
                name={t('financeiq.chart.legend.cashOutflows')}
                stackId="stack"
                fill={CHART_COLORS.CASH_OUTFLOWS}
                maxBarSize={16}
              >
                {data.map((d) => (
                  <Cell key={d.periodStart} fillOpacity={d.periodStart < todayString ? 0.4 : 1} />
                ))}
              </Bar>
            )}

            <Legend
              verticalAlign="bottom"
              height={36}
              wrapperStyle={{ paddingTop: '20px' }}
              content={({ payload }) => (
                <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1">
                  {payload
                    ?.filter((entry) => activeLegendLabels.has(entry.value))
                    .map((entry) => (
                      <div key={entry.value} className="flex items-center gap-1">
                        <svg width="10" height="10">
                          <rect width="10" height="10" fill={entry.color} rx="1" />
                        </svg>
                        <span className="text-xs font-medium text-gray-900">{entry.value}</span>
                      </div>
                    ))}
                </div>
              )}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default FinanceIQChart;
